/**
 * THE SIGN-IN REQUIREMENT COUNTDOWN'S RULE (docs/PLAN-signin-requirements-20261029.md, "The screens" 4; the contract:
 * "announced before enforced", "counted down", "never a dead end").
 *
 * A dated requirement the person has not met is told in the banner band, louder as its day comes: calm and dismissible
 * for the day while more than COUNTDOWN_LOUD_DAYS days remain; not dismissible from then; critical on the day itself
 * (the last day it can be done before the wall), and after it. The day is the doors' (noon UTC of it: the same day in
 * every zone), and how many days remain is counted in the reader's own calendar, so "on the day" is the day they read.
 *
 * Two sources carry the countdown, one notice per requirement: SignInRequirementBanner.tsx carries a workspace's two-step
 * requirement (its one action, "set it up", leads to Settings' two-step card), and the email notice
 * (EmailVerifyBanner.tsx) carries the platform's email requirement with its own action, the confirmation link. Both
 * take their loudness from here. Pure, so the rule is tested without Firebase; the words are signInRequirementCopy.ts.
 *
 * On the first download (the band's sources are), so it imports nothing but types and the pure day arithmetic.
 */
import type { BannerTone } from './GlobalBannerCenter';
import type { RequirementView, SignInSecurityStatus } from '../../lib/signInSecurity/signInSecurityAnswers';
import type { RequirementKind } from '../../types/signInSecurityVocabulary';
import { daysToDue } from '../../lib/signInSecurity/signInDates';

/** From this many days left (and fewer), the notice can no longer be dismissed for the day. */
export const COUNTDOWN_LOUD_DAYS = 3;

/**
 * How loud a dated requirement is now: `calm` (more than COUNTDOWN_LOUD_DAYS days left), `soon` (that many or fewer),
 * `today` (its day, in the reader's calendar), `overdue` (its day has passed, or the doors say so).
 */
export type CountdownStage = 'calm' | 'soon' | 'today' | 'overdue';

export function countdownStage(r: Pick<RequirementView, 'state'> & { dueAtMs: number }, nowMs: number): CountdownStage {
  if (r.state === 'overdue') return 'overdue';
  const left = daysToDue(r.dueAtMs, nowMs);
  if (left < 0) return 'overdue';
  if (left === 0) return 'today';
  if (left <= COUNTDOWN_LOUD_DAYS) return 'soon';
  return 'calm';
}

/** The band's tone for a stage: critical on the day and after it, a warning while it is near, else the source's own. */
export function countdownTone(stage: CountdownStage, calm: BannerTone): BannerTone {
  if (stage === 'today' || stage === 'overdue') return 'critical';
  if (stage === 'soon') return calm === 'critical' ? 'critical' : 'warn';
  return calm;
}

/** Only a calm notice may be dismissed, and only for the day (the band's daily rule). */
export function countdownDismissible(stage: CountdownStage): boolean {
  return stage === 'calm';
}

/** A requirement with its day, as the countdown reads it. */
export type DatedRequirement = RequirementView & { dueAtMs: number };

/**
 * The person's most pressing requirement of a kind: not met, with its day; one past its day first, then the earliest.
 * Null when there is none (or no status was read).
 */
export function pressingRequirement(status: SignInSecurityStatus | null, kind: RequirementKind): DatedRequirement | null {
  if (!status) return null;
  const dated = status.requirements.filter((r): r is DatedRequirement => r.kind === kind && r.state !== 'met' && r.dueAtMs !== null);
  if (dated.length === 0) return null;
  return [...dated].sort((a, b) => (a.state === 'overdue' ? 0 : 1) - (b.state === 'overdue' ? 0 : 1) || a.dueAtMs - b.dueAtMs)[0];
}

/** The kind the countdown banner carries (the email requirement is the email notice's own: one notice per requirement). */
export const COUNTDOWN_KIND: RequirementKind = 'two-step';

/** What the band knows before deciding whether the countdown shows. */
export interface CountdownFacts {
  /** Someone is signed in (not anonymously). */
  signedIn: boolean;
  /** The client lever (signInRequirementsShow) admits the tab's workspace. */
  leverAdmits: boolean;
  /** The band is on the app's pages. */
  onAppSurface: boolean;
  /** The requirement it would show (pressingRequirement), or none. */
  requirement: DatedRequirement | null;
  /** Its stage now. */
  stage: CountdownStage | null;
  /** The person dismissed the calm notice for the day. */
  dismissedToday: boolean;
}

/** Whether the countdown shows: a requirement to tell, where it may be told, and not dismissed while it may be. */
export function countdownShows(f: CountdownFacts): boolean {
  if (!f.signedIn || !f.leverAdmits || !f.onAppSurface || !f.requirement || !f.stage) return false;
  return !(countdownDismissible(f.stage) && f.dismissedToday);
}
